import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, Package, RotateCcw, CreditCard, User, Coins, Video, GraduationCap, Store, LifeBuoy, MessageCircle } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { api } from "../lib/api";

const ICONS = {
  package: Package, "rotate-ccw": RotateCcw, "credit-card": CreditCard, user: User,
  coins: Coins, video: Video, "graduation-cap": GraduationCap, store: Store,
};

export const HelpCenter = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/help/categories").then(({ data }) => setCategories(data.categories || [])); }, []);
  useEffect(() => {
    const params = {};
    if (active) params.category = active;
    if (query.trim()) params.q = query.trim();
    const t = setTimeout(() => {
      api.get("/help/articles", { params }).then(({ data }) => setArticles(data.articles || []));
    }, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [active, query]);

  const catLabel = useMemo(() => Object.fromEntries(categories.map((c) => [c.key, c.label])), [categories]);

  return (
    <div data-testid="help-center">
      <PageHero eyebrow="TAVEN Support" title="Help Center" subtitle="Find answers about orders, returns, payments, TAVEN Coins, live shopping, courses and more.">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="help-search"
            placeholder="Search help articles…" className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-taven-gold" />
        </div>
      </PageHero>

      <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-5xl">
        {!query && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="help-categories">
            <button onClick={() => setActive("")} data-testid="help-cat-all"
              className={`border p-4 text-left transition-colors ${active === "" ? "border-taven-gold bg-taven-gold/5" : "border-black/10 hover:border-taven-gold/60"}`}>
              <LifeBuoy className="w-5 h-5 text-taven-gold" />
              <p className="text-sm font-bold mt-2">All Topics</p>
            </button>
            {categories.map((c) => {
              const Icon = ICONS[c.icon] || LifeBuoy;
              return (
                <button key={c.key} onClick={() => setActive(c.key)} data-testid={`help-cat-${c.key}`}
                  className={`border p-4 text-left transition-colors ${active === c.key ? "border-taven-gold bg-taven-gold/5" : "border-black/10 hover:border-taven-gold/60"}`}>
                  <Icon className="w-5 h-5 text-taven-gold" />
                  <p className="text-sm font-bold mt-2 leading-tight">{c.label}</p>
                  <p className="text-[11px] text-muted-foreground">{c.count} article{c.count !== 1 ? "s" : ""}</p>
                </button>
              );
            })}
          </div>
        )}

        <h2 className="text-xl font-black font-display mb-4">
          {query ? `Results for “${query}”` : active ? catLabel[active] : "Frequently Asked Questions"}
        </h2>

        <div className="border border-black/10 divide-y divide-black/5" data-testid="help-articles">
          {articles.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No articles found. Try a different search or contact support.</p>}
          {articles.map((a) => (
            <div key={a.slug} data-testid={`help-article-${a.slug}`}>
              <button onClick={() => setOpen(open === a.slug ? null : a.slug)} data-testid={`help-toggle-${a.slug}`}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-taven-gray/40 transition-colors">
                <span className="text-sm font-bold">{a.question}</span>
                <ChevronDown className={`w-4 h-4 shrink-0 text-taven-gold transition-transform ${open === a.slug ? "rotate-180" : ""}`} />
              </button>
              {open === a.slug && (
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed" data-testid={`help-answer-${a.slug}`}>
                  {a.answer}
                  {query && <span className="block mt-1 text-[11px] uppercase tracking-wider text-taven-gold">{catLabel[a.category] || a.category}</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 border border-taven-gold/40 bg-taven-gold/5 p-6 flex items-center justify-between flex-wrap gap-3" data-testid="help-contact-cta">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-taven-gold" />
            <div>
              <p className="font-bold font-display">Still need help?</p>
              <p className="text-sm text-muted-foreground">Our team is here for you at support@taven.store.</p>
            </div>
          </div>
          <Link to="/contact" data-testid="help-contact-btn" className="bg-taven-black text-white text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-taven-orange transition-colors">Contact TAVEN Support</Link>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
